import { Button, Card, Form } from 'react-bootstrap'
import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import api from '../api'
import { useQuery, useQueryClient } from 'react-query'
import Markdown from 'react-markdown'
import rehypeKatex from 'rehype-katex'
import remarkMath from 'remark-math'
import remarkGfm from 'remark-gfm'
import 'katex/dist/katex.min.css'

import { ConferenceRoomInput, GrantInput, ImageInput, InputRow, DateInput, MultipleSelectInput, InstitutionInput, BooleanInput, StringInput, TextInput } from '../components/Input'
import { PrefixProvider } from './PrefixProvider'
import Loading from '../components/Loading'
import { setter, useEngine } from '../Engine'
import { SelectPeopleBlock } from './SelectPeopleBlock'

export default function Conference() {
    const { id } = useParams()

    const { isLoading, error, data } = useQuery([ 'process', 'conference', id ], async function () {
        var conference = { 
            title: "", 
            startDate: null,
            endDate: null,
            SSD: [],
            url: "",
            conferenceRoom: null,
            institution: null,
            isOutreach: null,
            grants: null, 
            flyerUrl: "",
            description: "",
        }

        if (id) {
            const res = await api.get(`/api/v0/process/conferences/get/${id}`)
            conference = res.data[0]

            // If the conference could not be loaded, then either it does not exist, or it 
            // was created by another user. Either way, we need to give an understandable
            // error to the end user. 
            if (! conference) {
                return;
            }

            conference._id = id
        }

        return {
            conference, 
            forbidden: !conference
        }            
    })

    if (isLoading) return <Loading error={error}></Loading>
    if (error) return <div>{`${error}`}</div>

    return <ConferenceBody conference={data.conference} forbidden={data.forbidden }/>
}

export function ConferenceBody({ conference, forbidden }) {
    const [data, setData] = useState(conference)
    const navigate = useNavigate()
    const queryClient = useQueryClient()

    if (forbidden) {
        return <div>
            <h4>Accesso negato</h4>
            <p>
                Il convegno selezionato non esiste, oppure è stato creato da un altro utente.
                Nel secondo caso, solo l'utente che l'ha originariamente creato (o un amministratore)  
                può modificarne il contenuto. 
            </p>
            <p>
                Nel caso sia necessario l'intervento di un amministratore, scrivere 
                all'indirizzo <a href="mailto:help@dm.unipi.it">help@dm.unipi.it</a>.
            </p>
        </div>
    }

    const onCompleted = async () => {
        // Insert the conference in the database
        await api.put('/api/v0/process/conferences/save', data)
        queryClient.invalidateQueries([ 'process', 'conference', data._id ])
        navigate('/process/conferences')
    }

    return <PrefixProvider value="process/conferences">
        <h1 className="text-primary pb-4">
            { conference._id 
                ? "Modifica convegno" 
                : "Inserimento nuovo convegno" }
        </h1>
        <ConferenceDetailsBlock 
            data={data} setData={setData}
            onCompleted={onCompleted}
            active={true}
        />
    </PrefixProvider>
}

export function ConferenceDetailsBlock({ onCompleted, data, setData, change, active, error }) {
    const ssdOptions = useEngine().Models.EventConference.schema.fields.SSD.enum
    const user = useEngine().user
    const isAdmin = user.roles && user.roles.includes('admin')

    const requirement = (()=>{
        if (!data.title || typeof data.title !== 'string' || data.title.trim() === "") return "Inserire il titolo del convegno"
        if (!data.startDate) return "Inserire la data di inizio del convegno"
        if (!data.endDate) return "Inserire la data di fine del convegno"
        if (new Date(data.startDate) > new Date(data.endDate)) return "Data di fine anteriore alla data di inizio"
        if (!data.organizers || data.organizers.length === 0) return "Inserire almeno un organizzatore per il convegno"
        return ""
    })()

    return <PrefixProvider value="process/conferences">
        <Card className="shadow">
            <Card.Header>
            <div className="d-flex d-row justify-content-between">
                    <div>
                        Dettagli del convegno
                    </div>
                    <div>
                    { isAdmin && data._id && <a href={`/event-conference/${data._id}`}>{data._id}</a>}    
                    { change && !active &&  
                        <Button className="text-end btn-warning btn-sm" onClick={change}>
                            Modifica
                        </Button>
                    }</div>
                </div>  
            </Card.Header>
            <Card.Body>
            { active ? <>
                <Form>
                    <InputRow label="Titolo" className="my-3">
                        <StringInput value={data.title} setValue={setter(setData,'title')}/>
                    </InputRow>
                    <InputRow label="Organizzatori" className="my-3">
                        <SelectPeopleBlock people={data.organizers || []} setPeople={people => setData(data => ({...data, organizers: people}))} prefix="process/conferences"/>
                    </InputRow>
                    <InputRow label="Data di inizio" className="my-3">
                        <DateInput value={data.startDate} setValue={setter(setData,'startDate')}/>
                    </InputRow>
                    <InputRow label="Data di fine" className="my-3">
                        <DateInput value={data.endDate} setValue={setter(setData, 'endDate')}/>
                    </InputRow>
                    <InputRow label="SSD" className="my-3">
                        <MultipleSelectInput 
                            value={data.SSD} 
                            setValue={setter(setData, "SSD")} 
                            options={ssdOptions}
                        />
                    </InputRow>
                    <InputRow className="my-3" label="Sito web"
                        help={<>
                            Se il convegno ha un sito web dedicato, inserirlo qui
                            e indirizzare gli utenti a esso nella descrizione.
                            Per inserire un link il formato è [descrizione](url)
                        </>}
                    >
                        <StringInput value={data.url} setValue={setter(setData,'url')} disableCreation={true}/>
                    </InputRow>
                    <InputRow className="my-3" label="Aula"
                        help={<>
                            Per la prenotazione di un'aula in Dipartimento si può procedere autonomamente
                            usando <a href="https://rooms.dm.unipi.it/">Rooms</a>,
                            oppure ci si può rivolgere alla segreteria
                        </>}
                    >
                        <ConferenceRoomInput value={data.conferenceRoom} setValue={setter(setData,'conferenceRoom')} disableCreation={true}/>
                    </InputRow>
                    <InputRow className="my-3" label="Istituzione (solo se diversa da unipi)"
                        help={<>
                            Se il convegno non si svolge al Dipartimento, selezionare un'istituzione
                            e lasciare vuota l'aula
                        </>}
                    >
                        <InstitutionInput value={data.institution} setValue={setter(setData,'institution')}/>
                    </InputRow>
                    <InputRow className="my-3" label="Terza missione"
                        help={<>
                            Se il convegno fa parte di un'iniziativa di Terza missione
                            selezionare la casella
                        </>}
                    >
                        <BooleanInput value={data.isOutreach} setValue={setter(setData,'isOutreach')}/>
                    </InputRow>
                    <InputRow className="my-3" label="Grant"
                        help={<>
                            Se utilizzate uno dei grant afferenti al Dipartimento, si prega di inserirlo
                            cercando tramite il nome del grant oppure il cognome del PI (nazionale)
                        </>}
                    >
                        <GrantInput multiple={true} value={data.grants || []} setValue={setter(setData,'grants')}/>
                    </InputRow>
                    <InputRow className="my-3" label="Locandina"
                        help={<>
                            Caricare un file PNG, JPG o PDF (massimo 10 MB per i PDF).
                            La locandina sarà pubblica e verrà mostrata prima della descrizione.
                        </>}
                    >
                        <ImageInput value={data.flyerUrl} setValue={setter(setData,'flyerUrl')} accept="image/png,image/jpeg,application/pdf" />
                    </InputRow>
                    <InputRow className="my-3" label="Descrizione"
                        help={<>
                            Si ricorda che potete scrivere sia in LaTex (utilizzando $ per le formule)
                            che in Markdown <a href="https://www.markdownguide.org/">https://www.markdownguide.org/</a>
                            <br />
                            L'anteprima della descrizione verrà mostrata più sotto mentre scrivi.
                            Se la locandina contiene già tutte le informazioni, probabilmente non serve aggiungere una descrizione.
                            Il testo resta utile per accessibilità e ricerca.
                        </>}
                    >
                        <TextInput value={data.description} setValue={setter(setData,'description')}/>
                    </InputRow>
                </Form>
                {error && <div className="alert alert-danger">{error}</div>}
                {requirement && <div className="alert alert-warning">{requirement}</div>}
                <div className="d-flex flex-row justify-content-end">
                    <Button className="text-end" onClick={onCompleted} disabled={requirement!==''}>Salva</Button>
                </div>
            </> : <>
                titolo: <b>{data.title}</b><br/>
                data inizio: <b>{data.startDate}</b><br/>
                data fine: <b>{data.endDate}</b><br/>
                SSD: <b>{data.SSD}</b><br/>
                sito web: <b>{data.url}</b><br/>
                aula: <b>{data.conferenceRoom && data.conferenceRoom.name}</b><br/>
                istituzione: <b>{data.institution}</b><br/>
                terza missione: <b>{data.isOutreach}</b><br/>
                grant: <b>{(data.grants && data.grants.map(g => g.name).join(', ')) || '---'}</b><br/>
                {data.flyerUrl && <><img src={data.flyerUrl} alt={`Locandina: ${data.title}`} style={{maxWidth: '100%', height: 'auto'}} /><br/></>}
                descrizione: <b>{data.description}</b><br/>
                creato da: <b>{data.createdBy?.username || data.createdBy?.email || '???'}</b><br/>
            </>}
            </Card.Body>

            {/* <Card.Footer>
                {JSON.stringify({data})}
            </Card.Footer> */}
        </Card>
            { data.description && 
                <Card className="shadow" style={{maxWidth:"60em"}}>
                    <Card.Header>Anteprima descrizione</Card.Header>
                    <Card.Body>
                    <Markdown remarkPlugins={[remarkMath, remarkGfm]} rehypePlugins={[rehypeKatex]}>{data.description}</Markdown>
                    </Card.Body>
                </Card>
            }
</PrefixProvider>
}
